/**
 * Overlay Component
 *
 * Renders a custom overlay on the Google Map.
 * Allows rendering React components at specific map coordinates.
 */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import CreateCustomOverlay from '../services/customOverlay';

function Overlay({ google, map, location, className, label, selected, onClick, onHover }) {
	const overlayRef = useRef(null);

	// Initialize overlay when google and map become available
	useEffect(() => {
		if (!map || !google) {
			return undefined;
		}

		const newOverlay = CreateCustomOverlay(google, map, {
			location,
			className,
			label,
			selected,
			onClick: onClick ? () => onClick(location) : undefined,
			onHover: onHover ? (loc) => onHover(loc) : undefined,
		});
		overlayRef.current = newOverlay;

		// Cleanup on unmount
		return () => {
			if (overlayRef.current) {
				overlayRef.current.remove();
				overlayRef.current = null;
			}
		};
	}, [google, map]); // Only create once

	// Update overlay when props change (including onClick so parent callback stays current)
	useEffect(() => {
		if (overlayRef.current) {
			overlayRef.current.update({
				className,
				label,
				selected,
				onClick: onClick ? () => onClick(location) : undefined,
				onHover: onHover ? (loc) => onHover(loc) : undefined,
			});
		}
	}, [className, label, selected, onClick, onHover, location]);

	return null;
}

Overlay.propTypes = {
	google: PropTypes.object,
	map: PropTypes.object,
	location: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		latitude: PropTypes.number.isRequired,
		longitude: PropTypes.number.isRequired,
	}).isRequired,
	className: PropTypes.string,
	label: PropTypes.node,
	selected: PropTypes.bool,
	onClick: PropTypes.func,
	onHover: PropTypes.func,
};

export default Overlay;
